import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { CryptoService } from './crypto.service';
import { TokenServiceService } from './token-service.service';
import { environment as ENV } from '../environments/environment';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OpenDialogYesOrNo } from './landing/dialog-yes-or-no/dialog-yes-or-no.component';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {

  private kccSessionExpiredShown = false;

  constructor(private cryptoService: CryptoService, private tokenService: TokenServiceService, private dialog: MatDialog) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Skip entirely if E2E encryption is disabled in environment
    if (!ENV.enableEncryption) {
      return next.handle(req);
    }

    // Skip encryption for key-exchange endpoint
    if (req.url.indexOf('/key-exchange') !== -1) {
      return next.handle(req);
    }

    // Skip if opted out via custom header
    if (req.headers.has('X-Skip-Encryption')) {
      var cleanReq = req.clone({
        headers: req.headers.delete('X-Skip-Encryption')
      });
      return next.handle(cleanReq);
    }

    // Skip if crypto session is not ready
    if (!this.cryptoService.isReady) {
      return next.handle(req);
    }

    var method = req.method.toUpperCase();
    var hasBody = (method === 'POST' || method === 'PUT' || method === 'PATCH') && req.body != null;

    // Multipart bodies cannot be encrypted: encrypt() runs JSON.stringify(), and
    // JSON.stringify(new FormData()) is "{}", which would silently wipe every field.
    // Send the FormData untouched (no Content-Type set, so the browser adds the
    // multipart boundary) but still decrypt the response.
    if (hasBody && req.body instanceof FormData) {
      var multipartReq = req.clone({
        headers: req.headers.set('x-session-id', this.cryptoService.sessionId)
      });
      return this.handleResponse(next.handle(multipartReq), req.url);
    }

    if (hasBody) {
      // Encrypt request body, then send and decrypt response
      return from(this.cryptoService.encrypt(req.body, req.url)).pipe(
        switchMap(function (encryptedBody) {
          var encryptedReq = req.clone({
            headers: req.headers.set('x-session-id', this.cryptoService.sessionId),
            body: encryptedBody
          });
          return this.handleResponse(next.handle(encryptedReq), req.url);
        }.bind(this))
      );
    } else {
      // No body to encrypt — just attach session header and decrypt response
      var headerReq = req.clone({
        headers: req.headers.set('x-session-id', this.cryptoService.sessionId)
      });
      return this.handleResponse(next.handle(headerReq), req.url);
    }
  }

  private handleResponse(source: Observable<HttpEvent<any>>, apiUrl: string): Observable<HttpEvent<any>> {
    var self = this;
    return source.pipe(
      switchMap(function (event) {
        if (event instanceof HttpResponse) {
          var body = event.body;
          if (body && body.encrypted === true && body.payload) {
            return from(self.cryptoService.decrypt(body.payload, apiUrl)).pipe(
              map(function (decrypted) {
                self.checkKccSessionExpired(decrypted);
                return event.clone({ body: decrypted });
              })
            );
          }
        }
        return from(Promise.resolve(event));
      })
    );
  }

  private checkKccSessionExpired(body: any): void {
    if (!this.tokenService.isKccSessionActive()) {
      return;
    }
    if (body && body.reason === 'SESSION_EXPIRED' && !this.kccSessionExpiredShown) {
      this.kccSessionExpiredShown = true;
      this.dialog.closeAll();
      var self = this;
      var dialogRef = this.dialog.open(OpenDialogYesOrNo, {
        disableClose: true,
        width: 'auto',
        height: 'auto',
        data: {
          content1: 'Session Expired',
          content2: 'Your session has expired. Please login again.',
          button1Text: 'OK'
        }
      });
      dialogRef.afterClosed().subscribe(function () {
        self.tokenService.clearKccToken();
        self.kccSessionExpiredShown = false;
        sessionStorage.clear();
        window.location.href = '/landing';
      });
      throw new Error('SESSION_EXPIRED');
    }
  }
}
